import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AlarmEventDto, AlarmLevel, AlarmListResponse, AlarmSettingsDto, UpdateAlarmSettingsInput } from '@atnt/shared';
import { api } from '@/shared/api/client';

/**
 * Alarms ("Mezon", apps/api/src/modules/alarms/routes.ts). Keys: `['tenant', id, 'alarms', …]`; the live feed
 * invalidates the whole subtree when an event opens, closes or is acknowledged.
 */

const base = (tenantId: string) => `/tenants/${encodeURIComponent(tenantId)}`;

export interface AlarmListParams {
  status: 'open' | 'closed' | 'all';
  level?: AlarmLevel;
  unacknowledged?: boolean;
  q?: string;
  page: number;
  pageSize: number;
}

export const alarmKeys = {
  all: (tenantId: string) => ['tenant', tenantId, 'alarms'] as const,
  list: (tenantId: string, p: AlarmListParams) => ['tenant', tenantId, 'alarms', 'list', p] as const,
  summary: (tenantId: string) => ['tenant', tenantId, 'alarms', 'summary'] as const,
  settings: (tenantId: string) => ['tenant', tenantId, 'alarms', 'settings'] as const,
};

export const useAlarmList = (tenantId: string, p: AlarmListParams) =>
  useQuery({
    queryKey: alarmKeys.list(tenantId, p),
    queryFn: () =>
      api.get<AlarmListResponse>(`${base(tenantId)}/alarms`, {
        query: {
          status: p.status,
          level: p.level,
          unacknowledged: p.unacknowledged ? 'true' : undefined,
          q: p.q,
          page: p.page,
          pageSize: p.pageSize,
        },
      }),
    placeholderData: keepPreviousData,
  });

/** Open-event counters (bell, KPI cards); polls slowly as a safety net behind the live feed. */
export const useAlarmSummary = (tenantId: string, enabled = true) =>
  useQuery({
    queryKey: alarmKeys.summary(tenantId),
    queryFn: () => api.get<AlarmListResponse>(`${base(tenantId)}/alarms`, { query: { status: 'open', pageSize: 5 } }),
    enabled,
    refetchInterval: 60_000,
  });

export const useAlarmSettings = (tenantId: string, enabled = true) =>
  useQuery({
    queryKey: alarmKeys.settings(tenantId),
    queryFn: () => api.get<AlarmSettingsDto>(`${base(tenantId)}/alarm-settings`),
    enabled,
    staleTime: 5 * 60_000,
  });

export function useUpdateAlarmSettings(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAlarmSettingsInput) => api.put<AlarmSettingsDto>(`${base(tenantId)}/alarm-settings`, { ...input }),
    onSuccess: (dto) => qc.setQueryData(alarmKeys.settings(tenantId), dto),
  });
}

/** "Tasdiqlash" — idempotent on the server (the first acknowledgement wins). */
export function useAcknowledgeAlarm(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<AlarmEventDto>(`${base(tenantId)}/alarms/${encodeURIComponent(id)}/ack`),
    onSuccess: () => qc.invalidateQueries({ queryKey: alarmKeys.all(tenantId) }),
  });
}
